import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import {
  useLeaderboard,
  type LeaderboardPeriod,
} from "../../../hooks/useGamificationAdmin";
import { useSeasons, useSeasonRankings } from "../../../hooks/useSeasons";
import type { SeasonEntry, SeasonRankingEntry } from "../../../server/seasons";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getLeaderboard } from "../../../server/gamification";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/leaderboards/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.gamification.leaderboard("week"),
      queryFn: async () => {
        const result = await getLeaderboard({
          data: { accessToken: session.access_token, period: "week", limit: 20 },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.aggregate,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Leaderboards"),
  component: LeaderboardsPage,
});

function LeaderboardsPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <AuthenticatedLeaderboards
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

const PERIODS: { value: LeaderboardPeriod; label: string }[] = [
  { value: "all_time", label: "All Time" },
  { value: "month", label: "This Month" },
  { value: "week", label: "This Week" },
  { value: "day", label: "Today" },
];

function AuthenticatedLeaderboards({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const auth = { userId, accessToken };
  const [period, setPeriod] = useState<LeaderboardPeriod>("all_time");
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);

  const RANK_COLORS: Record<number, string> = {
    1: "text-yellow-500",
    2: "text-gray-400",
    3: "text-amber-600",
  };
  const getRankColor = (rank: number) => RANK_COLORS[rank] ?? "text-muted-foreground";

  const {
    data: leaderboard,
    isLoading: leaderboardLoading,
    error: leaderboardError,
  } = useLeaderboard(auth, { period, limit: 25 });

  const { data: seasonsData, isLoading: seasonsLoading } = useSeasons({ accessToken });
  const { data: seasonRankings, isLoading: seasonRankingsLoading } = useSeasonRankings(
    { accessToken },
    selectedSeason
  );

  const activeSeason = seasonsData?.seasons.find((s: SeasonEntry) => s.is_active);

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Leaderboards</h1>
            <p className="text-muted-foreground">
              Performance rankings and competitive standings
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/gamification">Back to Gamification</Link>
          </Button>
        </header>

        {/* Main Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle>XP Leaderboard</CardTitle>
            <CardDescription>Top performers by experience points</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {PERIODS.map((p) => (
                <Button
                  key={p.value}
                  variant={period === p.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPeriod(p.value)}
                >
                  {p.label}
                </Button>
              ))}
            </div>

            {leaderboardLoading && (
              <p className="text-muted-foreground">Loading leaderboard...</p>
            )}
            {leaderboardError && (
              <p className="text-destructive">Error: {leaderboardError.message}</p>
            )}
            {!leaderboardLoading && !leaderboardError && (!leaderboard || leaderboard.length === 0) && (
              <p className="text-muted-foreground">No entries yet.</p>
            )}
            {!leaderboardLoading && !leaderboardError && leaderboard && leaderboard.length > 0 && (
              <ol className="space-y-2">
                {leaderboard.map((entry) => {
                  const isCurrentUser = entry.user_id === userId;
                  return (
                    <li
                      key={entry.user_id}
                      className={`flex items-center justify-between rounded-lg border p-3 ${
                        isCurrentUser ? "border-primary/50 bg-primary/5" : "bg-card"
                      } ${entry.rank <= 3 ? "ring-1 ring-yellow-500/20" : ""}`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`text-lg font-bold w-8 text-center ${getRankColor(entry.rank)}`}
                        >
                          #{entry.rank}
                        </span>
                        <div>
                          <p className="font-medium">
                            {entry.display_name || entry.full_name || entry.username}
                            {isCurrentUser && (
                              <span className="text-xs text-primary ml-1">(you)</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {entry.current_streak} day streak · {entry.tasks_completed} tasks
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{entry.xp.toLocaleString()} XP</p>
                        {period === "all_time" && entry.xp_this_week > 0 && (
                          <p className="text-xs text-muted-foreground">
                            +{entry.xp_this_week} this week
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>

        {/* Season Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle>Season Leaderboard</CardTitle>
            <CardDescription>
              {activeSeason
                ? `Current: ${activeSeason.season_name}`
                : "No active season"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {seasonsLoading && (
              <p className="text-muted-foreground">Loading seasons...</p>
            )}
            {!seasonsLoading && seasonsData && (
              <>
                <div className="flex flex-wrap gap-2">
                  {seasonsData.seasons.slice(0, 5).map((s: SeasonEntry) => (
                    <Button
                      key={s.id}
                      variant={selectedSeason === s.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedSeason(s.id)}
                    >
                      {s.season_name}
                      {s.is_active && " ●"}
                    </Button>
                  ))}
                </div>

                {selectedSeason && (
                  <>
                    {seasonRankingsLoading && (
                      <p className="text-muted-foreground">Loading rankings...</p>
                    )}
                    {!seasonRankingsLoading && (!seasonRankings || seasonRankings.length === 0) && (
                      <p className="text-muted-foreground">No season rankings yet.</p>
                    )}
                    {!seasonRankingsLoading && seasonRankings && seasonRankings.length > 0 && (
                      <ol className="space-y-2">
                        {seasonRankings.map((r: SeasonRankingEntry) => (
                          <li
                            key={r.user_id}
                            className="flex items-center justify-between rounded-lg border bg-card p-3"
                          >
                            <span className="font-medium">
                              #{r.rank} {r.full_name || r.username}
                            </span>
                            <span className="text-muted-foreground">
                              {r.season_xp.toLocaleString()} XP · Level {r.season_level}
                            </span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </>
                )}
                {!selectedSeason && activeSeason && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedSeason(activeSeason.id)}
                  >
                    View Current Season Rankings
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
